import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { cn } from '@tempo-io/sui-class-utils';
import { addDays, format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { LabelWithField } from '@/common/components/ui/swimlane/types';
import { useItemSettleMarks } from '@/common/queries/useItemSettleMarks';
import { useParticipatesInTreeSetSettleTracking } from '@/common/queries/useParticipatesInTreeSetSettleTracking';
import { toISOTimestamp } from '@/common/utils/dateFormats';
import { useItemPanelNavigation } from '@/features/item-panel/hooks/useItemPanelParams';
import { VisualizationRanksApi } from '@/features/visualizations/api/visualizationRanks';
import { useTimelineHeaderVisibilityMutation } from '@/features/visualizations/mutations/useTimelineLayoutMutations';
import { visualizationQueryKeys } from '@/features/visualizations/queryKeys';
import { DEFAULT_BASE_COLOR } from '@/features/visualizations/utils/colors';
import { usePortalContainer } from '../PortalContainerContext';
import { useSystemProgress } from '../utils/useSystemProgress';
import { TimelineRowAxisLabel } from './layout/TimelineRowAxisLabel';
import { TimelineRowHeaderResize } from './layout/TimelineRowHeaderResize';
import { TimelineDroppableRow } from './TimelineDroppableRow';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from './TimelineItem';
import { TimelineItemDragOverlayContent } from './TimelineItemDragOverlay';
import { TimelineTodayMarker } from './TimelineTodayMarker';
import {
  DEFAULT_ROW_HEADER_WIDTH,
  PARENT_HEADER_HEIGHT,
  useTimelineColumnDayWidth,
  VERTICAL_ROW_HEADER_WIDTH,
} from './useTimelineColumnDayWidth';
import type { SelectionClickModifiers } from './useTimelineSelection';
import {
  type TimelineRowType,
  type TimelineTableCellContext,
  useTimelineTableColumns,
} from './useTimelineTableColumns';
import { collectRenderedItemIds } from './utils/collectRenderedItemIds';
import { timelineBgColorWithAlpha } from './utils/timelineColorUtils';
import {
  computeOptimisticRank,
  isUpdatableFieldId,
  renderedDayOf,
  shouldHandleEvent,
} from './utils/timelineDragUtils';
import {
  calculateChildLanes,
  calculateItemOffset,
  resolveDropNeighbors,
  sortByDateThenId,
} from './utils/timelineStackingUtils';

class CustomPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: ({ nativeEvent: event }: { nativeEvent: PointerEvent }) => {
        return shouldHandleEvent(event.target as HTMLElement);
      },
    },
  ];
}

const BASE_ROW_OVERSCAN = 5;
const HEADER_CELL_HEIGHT = 32;

type ExpectedSettleDays = { start: string | null; end: string | null };

export const TimelineTable = ({
  from,
  to,
  data,
  canEdit,
  isPublishedMode,
  onItemDrop,
  onItemResize,
  header1FieldId,
  header2FieldId,
  scrollContainerRef,
  layout,
  expandedRows,
  setExpandedRows,
  itemColorById,
  itemLabelsById,
  rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
  onRowHeaderWidthChange,
  expandedChildren,
  setExpandedChildren,
  setReorderHeadersModalState,
  onItemClick: onItemClickProp,
  viewId,
  visualizationId,
  itemRanks,
  isExportMode = false,
  selectedIds,
  previewIds,
  onSelectionClick,
  onVisibleItemIdsChange,
  treeSetQueryKey,
  readOnly,
}: {
  from: string;
  to: string;
  data: TimelineRowType[];
  canEdit: boolean;
  isPublishedMode: boolean;
  onItemDrop?: (
    itemId: number,
    row: TimelineRowType,
    newStartDate: string,
    newEndDate: string,
    sourceRow?: TimelineRowType,
  ) => void;
  onItemResize: (itemId: number, newStartDate: string, newEndDate: string) => void;
  header1FieldId: string;
  header2FieldId: string;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  expandedRows: ExpandedState;
  setExpandedRows: (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => void;
  layout?: TimelineLayoutDTO;
  itemColorById?: Record<number, string>;
  itemLabelsById?: Map<number, LabelWithField[]>;
  rowHeaderWidth?: number;
  onRowHeaderWidthChange?: (width: number) => void;
  expandedChildren: { [key: number]: boolean };
  setExpandedChildren: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
  setReorderHeadersModalState: React.Dispatch<
    React.SetStateAction<{
      isReorderingMainHeader: boolean;
      parentHeaderId: string | null;
    } | null>
  >;
  onItemClick?: (itemId: number) => void;
  viewId: number;
  visualizationId: number;
  itemRanks?: Map<number, string>;
  isExportMode?: boolean;
  selectedIds?: ReadonlySet<number>;
  previewIds?: ReadonlySet<number> | null;
  onSelectionClick?: (itemId: number, modifiers: SelectionClickModifiers) => boolean;
  onVisibleItemIdsChange?: (itemIds: ReadonlySet<number>) => void;
  treeSetQueryKey?: readonly unknown[];
  readOnly: boolean;
}) => {
  const queryClient = useQueryClient();
  const { hideHeaderMutation } = useTimelineHeaderVisibilityMutation(viewId, visualizationId);
  const portalContainer = usePortalContainer();
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const { openItemPanel } = useItemPanelNavigation();
  const [activeDragData, setActiveDragData] = useState<{
    color: string;
    name: string;
    itemId: number;
    duration: number;
    startDate: string;
    endDate: string;
    isCompact?: boolean;
    currentRow?: TimelineRowType;
    selectedItemIds?: number[] | null;
    selectedPreviews?: {
      itemId: number;
      dx: number;
      dy: number;
      width: number;
      height: number;
      color: string;
      name: string;
    }[];
  } | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [forbiddenRowId, setForbiddenRowId] = useState<string | null>(null);
  // Items just dragged or resized, hidden until the rendered data reflects
  // their new dates so they never flash at their old spot while the
  // optimistic update settles.
  const [settlingItemIds, setSettlingItemIds] = useState<ReadonlySet<number>>(() => new Set());
  const pendingSettleDatesRef = useRef<Map<number, ExpectedSettleDays>>(new Map());
  const settleBackstopTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const timeouts = settleBackstopTimeoutsRef.current;
    return () => {
      for (const id of timeouts) clearTimeout(id);
    };
  }, []);

  // Hide the given items until the rendered start and end dates match the
  // expected post-update days. Shared by drag-drop and resize; a backstop
  // reveals them even if a drop/resize is rolled back.
  const markItemsSettling = useCallback((expectedDaysById: Map<number, ExpectedSettleDays>) => {
    if (expectedDaysById.size === 0) return;
    const ids = [...expectedDaysById.keys()];
    for (const [id, days] of expectedDaysById) {
      pendingSettleDatesRef.current.set(id, days);
    }
    setSettlingItemIds((prev) => new Set([...prev, ...ids]));
    const timeoutId = setTimeout(() => {
      settleBackstopTimeoutsRef.current.delete(timeoutId);
      setSettlingItemIds((prev) => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        for (const id of ids) {
          next.delete(id);
          pendingSettleDatesRef.current.delete(id);
        }
        return next;
      });
    }, 500);
    settleBackstopTimeoutsRef.current.add(timeoutId);
  }, []);

  const handleItemResize = useCallback(
    (itemId: number, newStartDate: string, newEndDate: string) => {
      markItemsSettling(
        new Map([
          [
            itemId,
            {
              start: renderedDayOf(toISOTimestamp(newStartDate)),
              end: renderedDayOf(toISOTimestamp(newEndDate)),
            },
          ],
        ]),
      );
      onItemResize(itemId, newStartDate, newEndDate);
    },
    [markItemsSettling, onItemResize],
  );

  const sensors = useSensors(
    useSensor(CustomPointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
  );

  const hasVerticalSubHeaders = useMemo(() => {
    return (
      layout?.headerDirection === 'vertical' &&
      data.length > 0 &&
      data[0]?.mainHeaderLabel !== undefined
    );
  }, [layout?.headerDirection, data]);

  const isVertical = layout?.headerDirection === 'vertical';
  const rowHeaderColumnWidth =
    isVertical && !hasVerticalSubHeaders ? VERTICAL_ROW_HEADER_WIDTH : rowHeaderWidth;

  const dayWidth = useTimelineColumnDayWidth({
    scrollContainerRef,
    hasVerticalSubHeaders,
    from,
    to,
    rowHeaderWidth: rowHeaderColumnWidth,
    isExportMode,
  });

  const onItemClick = useCallback(
    (itemId: number) => {
      if (onItemClickProp) {
        onItemClickProp(itemId);
      } else {
        openItemPanel({ itemId });
      }
    },
    [onItemClickProp, openItemPanel],
  );

  const onHideHeader = useCallback(
    (headerId: string, isMainHeader: boolean, parentHeaderId?: string) => {
      const headerFieldId = isMainHeader ? header1FieldId : header2FieldId;
      if (!headerFieldId) return;
      hideHeaderMutation.mutate({
        headerFieldId,
        headerId,
        parentFieldHeaderId: !isMainHeader ? header1FieldId : null,
        parentHeaderId: parentHeaderId ?? null,
      });
    },
    [header1FieldId, header2FieldId, hideHeaderMutation],
  );

  const hasUpdatableHeader =
    isUpdatableFieldId(header1FieldId) || (!!header2FieldId && isUpdatableFieldId(header2FieldId));
  const canDragBetweenHeaders = canEdit && hasUpdatableHeader;
  const canDragDates = canEdit;
  const canResize = canEdit;

  const isSystemProgress = layout?.progressFieldType === 'system';
  const itemIds = useMemo(() => {
    const collectIds = (rows: TimelineRowType[]): number[] =>
      rows.flatMap((row) => [
        ...(row.allItems ?? []).map((item) => item.id),
        ...collectIds(row.subRows ?? []),
      ]);
    return collectIds(data);
  }, [data]);
  const { progressMap: systemProgressMap } = useSystemProgress({
    itemIds,
    enabled: isSystemProgress,
  });

  const itemsById = useMemo(() => {
    const map = new Map<
      number,
      { startDate: string | null; endDate: string | null; row: TimelineRowType }
    >();
    const visit = (rows: TimelineRowType[]) => {
      for (const row of rows) {
        for (const item of row.allItems ?? []) {
          map.set(item.id, {
            startDate: item.plannedStartDateTime ?? null,
            endDate: item.plannedFinishDateTime ?? null,
            row,
          });
        }
        if (row.subRows) visit(row.subRows);
      }
    };
    visit(data);
    return map;
  }, [data]);

  const participatesInSettleTracking =
    useParticipatesInTreeSetSettleTracking(readOnly) && treeSetQueryKey !== undefined;
  const renderedItemIds = useMemo(() => collectRenderedItemIds(data), [data]);
  const settleMarkById = useItemSettleMarks(renderedItemIds, participatesInSettleTracking);

  const columns = useTimelineTableColumns({
    from,
    to,
    canEdit,
    canResize,
    canDragBetweenHeaders,
    canDragDates,
    isPublishedMode,
    isExportMode,
    isVertical,
    hasVerticalSubHeaders,
    hasSubHeaders: Boolean(layout?.header2),
    titleSize: layout?.titleSize ?? 'medium',
    progressFieldId: isSystemProgress ? null : layout?.progressFieldId || null,
    systemProgressMap: isSystemProgress ? systemProgressMap : null,
    dayWidth,
    expandedChildren,
    showChildItems: layout?.showChildItems ?? false,
    setExpandedChildren,
    onResizeEnd: handleItemResize,
    itemColorById,
    itemLabelsById,
    isCompact: layout?.theme === 'compact',
    rowHeaderWidth,
    onItemClick,
    setReorderHeadersModalState,
    onHideHeader: canEdit && !isPublishedMode ? onHideHeader : undefined,
    selectedIds,
    previewIds,
    settlingItemIds,
    settleMarkById,
    onSelectionClick,
  });

  useEffect(() => {
    if (settlingItemIds.size === 0) return;
    const expectedDates = pendingSettleDatesRef.current;
    const settled: number[] = [];
    for (const id of settlingItemIds) {
      const entry = itemsById.get(id);
      const expected = expectedDates.get(id);
      const isSettled =
        !entry ||
        !expected ||
        (renderedDayOf(entry.startDate) === expected.start &&
          renderedDayOf(entry.endDate) === expected.end);
      if (isSettled) settled.push(id);
    }
    if (settled.length === 0) return;
    setSettlingItemIds((prev) => {
      const next = new Set(prev);
      for (const id of settled) {
        next.delete(id);
        expectedDates.delete(id);
      }
      return next;
    });
  }, [itemsById, settlingItemIds]);

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded: expandedRows,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    onExpandedChange: setExpandedRows,
    getSubRows: (row) => row.subRows,
    getRowId: (row) => row.id,
  });

  const tableRows = table.getRowModel().rows;
  const numFixedColumns = hasVerticalSubHeaders ? 2 : 1;

  const isDropAllowed = useCallback(
    (sourceRow: TimelineRowType | undefined, targetRowId: string | undefined): boolean => {
      if (!targetRowId) return false;
      const targetRow = tableRows.find((r) => r.id === targetRowId);
      if (!targetRow) return false;

      if (sourceRow && targetRow.original.id === sourceRow.id) {
        return canDragDates;
      }

      if (!canDragBetweenHeaders) return false;

      const isHeader1Updatable = isUpdatableFieldId(header1FieldId);
      const isHeader2Updatable = !!header2FieldId && isUpdatableFieldId(header2FieldId);
      const hasHeader2 = !!header2FieldId;

      if (isHeader1Updatable && isHeader2Updatable) {
        return true;
      }

      if (isHeader1Updatable && !hasHeader2) {
        return true;
      }

      if (isHeader1Updatable && hasHeader2) {
        if (!sourceRow) return true;
        const sourceSubHeaderValue = sourceRow.parentHeaderId ? sourceRow.headerId : null;
        const targetSubHeaderValue = targetRow.original.parentHeaderId
          ? targetRow.original.headerId
          : null;
        return targetSubHeaderValue === null || sourceSubHeaderValue === targetSubHeaderValue;
      }

      if (!isHeader1Updatable && isHeader2Updatable) {
        if (!sourceRow) return true;
        const sourceParentHeaderValue = sourceRow.parentHeaderId ?? sourceRow.headerId;
        const targetParentHeaderValue =
          targetRow.original.parentHeaderId ?? targetRow.original.headerId;
        return sourceParentHeaderValue === targetParentHeaderValue;
      }

      return false;
    },
    [canDragBetweenHeaders, canDragDates, tableRows, header1FieldId, header2FieldId],
  );

  const rowGroupMetadata = useMemo(() => {
    if (!hasVerticalSubHeaders) return null;

    const groups: Array<{ firstRowIndex: number; lastRowIndex: number }> = [];

    for (let i = 0; i < tableRows.length; i++) {
      const row = tableRows[i];
      if (row?.original.isFirstSubRow) {
        const subRowCount = row.original.subRowCount ?? 1;
        groups.push({
          firstRowIndex: i,
          lastRowIndex: i + subRowCount - 1,
        });
      }
    }

    return groups;
  }, [tableRows, hasVerticalSubHeaders]);

  const rowRangeExtractor = useMemo(() => {
    if (!hasVerticalSubHeaders || !rowGroupMetadata) {
      return undefined;
    }

    return (range: { startIndex: number; endIndex: number }) => {
      const { startIndex, endIndex } = range;
      const indicesToInclude = new Set<number>();

      for (let i = startIndex; i <= endIndex; i++) {
        indicesToInclude.add(i);
      }

      for (const group of rowGroupMetadata) {
        if (group.lastRowIndex >= startIndex && group.firstRowIndex <= endIndex) {
          for (let i = group.firstRowIndex; i <= group.lastRowIndex; i++) {
            indicesToInclude.add(i);
          }
        }
      }

      return Array.from(indicesToInclude).sort((a, b) => a - b);
    };
  }, [hasVerticalSubHeaders, rowGroupMetadata]);

  const laneHeight = layout?.theme === 'compact' ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;
  const rowGap = layout?.theme === 'compact' ? 2 : 0;
  const estimatedRowHeight = laneHeight + rowGap;

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: BASE_ROW_OVERSCAN,
    ...(rowRangeExtractor && { rangeExtractor: rowRangeExtractor }),
  });

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type !== 'timeline-item') return;

    const {
      color,
      name,
      itemId,
      duration,
      startDate,
      endDate,
      isCompact,
      currentRow,
      selectedItemIds,
    } = active.data.current;

    const idsForPreview: number[] = selectedItemIds?.length ? selectedItemIds : [itemId];
    const anchorEl = document.querySelector<HTMLElement>(`[data-timeline-item-id="${itemId}"]`);
    const anchorRect = anchorEl?.getBoundingClientRect();

    const selectedPreviews = anchorRect
      ? idsForPreview.flatMap((id) => {
          const el = document.querySelector<HTMLElement>(`[data-timeline-item-id="${id}"]`);
          if (!el) return [];
          const r = el.getBoundingClientRect();
          return [
            {
              itemId: id,
              dx: r.left - anchorRect.left,
              dy: r.top - anchorRect.top,
              width: r.width,
              height: r.height,
              color: el.dataset.timelineItemColor ?? color,
              name: el.dataset.timelineItemName ?? '',
            },
          ];
        })
      : undefined;

    setActiveDragData({
      color,
      name,
      itemId,
      duration,
      startDate,
      endDate,
      isCompact,
      currentRow,
      selectedItemIds,
      selectedPreviews,
    });
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over, active } = event;
      const targetRowId = over?.id as string | null;
      const sourceRow = active?.data?.current?.currentRow as TimelineRowType | undefined;
      if (targetRowId && !isDropAllowed(sourceRow, targetRowId)) {
        setHoveredRowId(null);
        setForbiddenRowId(targetRowId);
        return;
      }
      setHoveredRowId(targetRowId);
      setForbiddenRowId(null);
    },
    [isDropAllowed],
  );

  const handleLaneDrop = useCallback(
    async (
      itemId: number,
      rowId: string,
      neighbours: { previousItemId?: number; nextItemId?: number },
    ) => {
      const row = tableRows.find((r) => r.id === rowId);
      if (!row) return;

      const allItems = row.original.allItems ?? [];
      // Reading order for rank seeding: lane by lane, and within a lane left to
      // right by date — so initialized LexoRanks match what is on screen.
      const sortedByOrder = [...allItems].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0) || sortByDateThenId(a, b),
      );

      const otherItems = sortedByOrder.filter((item) => item.id !== itemId);
      if (otherItems.length === 0) return;

      const { previousItemId, nextItemId } = neighbours;

      const hasUnrankedAdjacent =
        (previousItemId !== undefined && !itemRanks?.has(previousItemId)) ||
        (nextItemId !== undefined && !itemRanks?.has(nextItemId));

      // Steady state (both boundaries already ranked): predict the new rank with
      // the same LexoRank math the backend uses and write it to the cache now, so
      // the re-stack settles to the dropped position without waiting for the PUT.
      // Skip when ranks must be initialized first — that seeding is server-owned.
      if (!hasUnrankedAdjacent) {
        const previousRank =
          previousItemId !== undefined ? itemRanks?.get(previousItemId) : undefined;
        const nextRank = nextItemId !== undefined ? itemRanks?.get(nextItemId) : undefined;
        const optimisticRank = computeOptimisticRank(previousRank, nextRank);
        if (optimisticRank) {
          queryClient.setQueryData<Map<number, string>>(
            visualizationQueryKeys.itemRanks.detail(viewId, visualizationId),
            (prev) => new Map(prev ?? []).set(itemId, optimisticRank),
          );
        }
      }

      if (hasUnrankedAdjacent) {
        await VisualizationRanksApi.initializeRanks(
          viewId,
          visualizationId,
          sortedByOrder.map((item) => item.id),
        );
      }

      await VisualizationRanksApi.updateItemRank(viewId, visualizationId, itemId, {
        previousItemId,
        nextItemId,
      });

      queryClient.invalidateQueries({
        queryKey: visualizationQueryKeys.itemRanks.detail(viewId, visualizationId),
      });
    },
    [tableRows, viewId, visualizationId, queryClient, itemRanks],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependency array is intentionally limited to prevent excessive re-renders
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta, over } = event;
      if (active.data.current?.type !== 'timeline-item' || !delta) {
        setActiveDragData(null);
        setForbiddenRowId(null);
        return;
      }

      const sourceRow = active?.data?.current?.currentRow as TimelineRowType | undefined;
      const daysOffset = Math.round(delta.x / dayWidth);
      const draggedItemId = active.data.current.itemId as number;
      const bulkSelectedIds = active.data.current.selectedItemIds as number[] | null;
      const itemIdsToUpdate = bulkSelectedIds ?? [draggedItemId];

      const rect = active.rect.current.translated;
      if (!rect || !scrollContainerRef.current) {
        setActiveDragData(null);
        setForbiddenRowId(null);
        return;
      }

      const targetRowId = over?.id as string | undefined;
      const targetRow =
        over?.data?.current?.dataIndex !== undefined
          ? tableRows[over.data.current.dataIndex]
          : undefined;

      if (targetRowId && !isDropAllowed(sourceRow, targetRowId)) {
        setActiveDragData(null);
        setHoveredRowId(null);
        setForbiddenRowId(null);
        return;
      }

      const shiftDates = (start: string | null, end: string | null) => {
        const effStart = start ?? end;
        const effEnd = end ?? start;
        if (!effStart || !effEnd) return null;
        return {
          start: format(addDays(new Date(effStart), daysOffset), 'yyyy-MM-dd'),
          end: format(addDays(new Date(effEnd), daysOffset), 'yyyy-MM-dd'),
        };
      };

      if (targetRow && onItemDrop) {
        const movedBetweenRows = targetRow.original.id !== sourceRow?.id;
        const expectedDaysById = new Map<number, ExpectedSettleDays>();
        for (const id of itemIdsToUpdate) {
          const entry = itemsById.get(id);
          if (!entry) continue;
          const dates = shiftDates(entry.startDate, entry.endDate);
          if (!dates) continue;
          const rowForItem = movedBetweenRows ? targetRow.original : entry.row;
          onItemDrop(id, rowForItem, dates.start, dates.end, entry.row);
          if (daysOffset !== 0 || movedBetweenRows) {
            expectedDaysById.set(id, {
              start: renderedDayOf(toISOTimestamp(dates.start)),
              end: renderedDayOf(toISOTimestamp(dates.end)),
            });
          }
        }
        markItemsSettling(expectedDaysById);

        if (itemIdsToUpdate.length === 1 && targetRowId && over?.rect) {
          // Resolve the rank neighbours from where the drop landed (lane + date),
          // mirroring how items are positioned, then commit that rank.
          const others = (targetRow.original.allItems ?? []).filter(
            (item) => item.id !== draggedItemId,
          );
          if (others.length > 0) {
            const dropLane = (rect.top + rect.height / 2 - over.rect.top) / laneHeight;
            const draggedStartDay = renderedDayOf(active.data.current.startDate as string);
            const dropStartMs = draggedStartDay
              ? new Date(draggedStartDay).getTime() + daysOffset * 24 * 60 * 60 * 1000
              : 0;
            const neighbours = resolveDropNeighbors(
              others,
              dropLane,
              dropStartMs,
              laneHeight,
              dayWidth,
            );
            if (neighbours.previousItemId !== undefined || neighbours.nextItemId !== undefined) {
              void handleLaneDrop(draggedItemId, targetRowId, neighbours).catch(() => {});
            }
          }
        }
      }

      setActiveDragData(null);
      setHoveredRowId(null);
      setForbiddenRowId(null);
    },
    [
      onItemDrop,
      dayWidth,
      data,
      scrollContainerRef,
      isDropAllowed,
      handleLaneDrop,
      laneHeight,
      tableRows,
      itemsById,
      markItemsSettling,
    ],
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragData(null);
    setHoveredRowId(null);
    setForbiddenRowId(null);
  }, []);

  const virtualItems = rowVirtualizer.getVirtualItems();

  const lastVisibleKeyRef = useRef<string>('');
  useEffect(() => {
    if (!onVisibleItemIdsChange) return;
    const visibleIds = new Set<number>();
    for (const virtualRow of virtualItems) {
      const row = tableRows[virtualRow.index];
      for (const item of row?.original.allItems ?? []) {
        visibleIds.add(item.id);
      }
    }
    const key = Array.from(visibleIds)
      .sort((a, b) => a - b)
      .join(',');
    if (key === lastVisibleKeyRef.current) return;
    lastVisibleKeyRef.current = key;
    onVisibleItemIdsChange(visibleIds);
  }, [virtualItems, tableRows, onVisibleItemIdsChange]);

  const virtualRows = isExportMode
    ? tableRows.map((_, index) => ({
        index,
        key: index,
        start: index * estimatedRowHeight,
        size: estimatedRowHeight,
      }))
    : virtualItems;
  const headerPadding = 6;
  const headerHeight = HEADER_CELL_HEIGHT * table.getHeaderGroups().length + headerPadding;
  const totalRowSize = rowVirtualizer.getTotalSize() + headerHeight;
  const virtualRowStart = isExportMode ? 0 : (virtualItems[0]?.start ?? 0);
  const tableHeight = isExportMode
    ? totalRowSize
    : virtualItems.length === 0
      ? Math.max(totalRowSize, 300)
      : totalRowSize;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      collisionDetection={pointerWithin}
      autoScroll={{ threshold: { x: 0, y: 0.2 } }}
    >
      <div
        className={cn('min-h-0 overflow-x-hidden', !isExportMode && 'overflow-y-auto')}
        ref={scrollContainerRef}
      >
        {onRowHeaderWidthChange && (
          <TimelineRowHeaderResize
            rowHeaderWidth={rowHeaderWidth}
            onRowHeaderWidthChange={onRowHeaderWidthChange}
            hasVerticalSubHeaders={hasVerticalSubHeaders}
            isVertical={isVertical}
            canEdit={canEdit}
            isPublishedMode={isPublishedMode}
            scrollContainerRef={scrollContainerRef}
          />
        )}
        <div className="flex relative pr-1" style={{ height: tableHeight }}>
          <TimelineRowAxisLabel scrollContainerRef={scrollContainerRef} layout={layout} />
          <div
            className="relative w-max"
            data-testid="timeline-container"
            ref={timelineContainerRef}
          >
            {layout?.showTodayLine && (
              <TimelineTodayMarker
                from={from}
                rowHeaderWidth={rowHeaderColumnWidth}
                dayWidth={dayWidth}
                tableHeight={tableHeight}
                scrollContainerRef={scrollContainerRef}
              />
            )}
            {/* Header groups */}
            <div
              className={cn('w-max flex z-5 bg-bg-surface-base', !isExportMode && 'sticky top-0')}
            >
              <div
                className={cn(
                  'bg-bg-surface-base flex flex-col mb-1',
                  !isExportMode && 'overflow-hidden',
                  !isExportMode && 'border-y border-border-neutral',
                )}
                data-export="column-header-container"
              >
                {table.getHeaderGroups().map((headerGroup, headerGroupIndex) => (
                  <div key={headerGroup.id} className="flex relative w-full">
                    {headerGroup.headers.map((header, headerIndex) => {
                      const width = header.getSize();
                      return (
                        <div
                          data-testid={`header-${header.id}`}
                          key={header.id}
                          className={cn('flex-1', {
                            'left-0 sticky bg-bg-surface-base z-10':
                              !isExportMode &&
                              ((headerGroupIndex === 0 && headerIndex === 0) ||
                                (headerGroupIndex === 1 && headerIndex < numFixedColumns)),
                          })}
                          style={{
                            minWidth: width,
                            width: width,
                            height: HEADER_CELL_HEIGHT,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Body rows */}
            <div
              className="flex flex-col relative h-max"
              style={{
                transform: `translateY(${virtualRowStart}px)`,
                overflow: 'visible',
              }}
            >
              {virtualRows.map((row) => {
                const rowCells = tableRows[row.index]?.getVisibleCells() || [];
                if (rowCells.length === 0) {
                  return null;
                }
                const originalRow = tableRows[row.index];
                const rowId = originalRow?.id || String(row.index);
                const isParent =
                  originalRow?.subRows && originalRow?.subRows.length > 0 && !hasVerticalSubHeaders;
                const sourceHeaderId = activeDragData?.currentRow?.headerId;
                const isDragTarget =
                  hoveredRowId === rowId ||
                  (sourceHeaderId !== undefined &&
                    originalRow?.original.headerId === sourceHeaderId);
                const allItems = originalRow?.original.allItems ?? [];
                const otherItemCount = activeDragData
                  ? allItems.filter((item) => item.id !== activeDragData.itemId).length
                  : 0;
                const extraLaneHeight =
                  activeDragData && isDragTarget && otherItemCount > 0 ? laneHeight : 0;
                const firstSubRowId = originalRow?.original.firstSubRowId;
                const isGroupCollapsed =
                  hasVerticalSubHeaders && firstSubRowId
                    ? !table.getRow(firstSubRowId).getIsExpanded()
                    : false;
                // When collapsed, only the first sub-row stays tall enough to host the
                // parent header (one lane); the rest of the group's rows shrink away.
                const collapsedHeight = originalRow?.original.isFirstSubRow ? laneHeight : 0;
                const baseMinHeight = isParent ? PARENT_HEADER_HEIGHT : laneHeight;
                const minHeight = isGroupCollapsed
                  ? collapsedHeight
                  : baseMinHeight + extraLaneHeight;
                const baseMaxHeight =
                  originalRow?.original.calculatedHeight ??
                  (originalRow?.original.maxOrder !== undefined
                    ? (originalRow.original.maxOrder + 1) * laneHeight
                    : undefined);
                const maxHeight = isGroupCollapsed
                  ? collapsedHeight
                  : baseMaxHeight !== undefined
                    ? baseMaxHeight + extraLaneHeight
                    : undefined;
                const isFirstSubRowOfGroup =
                  hasVerticalSubHeaders && originalRow?.original.isFirstSubRow;
                const fixedColumns = rowCells.slice(0, numFixedColumns);
                const visibleDateCells = rowCells.slice(numFixedColumns);

                // Full-row background framing the expanded parent and its children:
                // spans the whole row (header + columns), from the parent's lane top
                // down to below the lowest child. Lane math mirrors the item cells
                // so it tracks the same stacking/push-down (which is left unchanged).
                // Single expansion means at most one parent per row is open, so
                // there is never a colliding-cluster offset to account for here.
                const expandedParent = layout?.showChildItems
                  ? allItems.find((item) => expandedChildren[item.id])
                  : undefined;
                const expandedBand =
                  expandedParent && !hasVerticalSubHeaders
                    ? {
                        top: calculateItemOffset(
                          expandedParent,
                          allItems,
                          expandedChildren,
                          laneHeight,
                        ),
                        height: calculateChildLanes(expandedParent) * laneHeight,
                        color:
                          itemColorById?.[expandedParent.id] ??
                          originalRow?.original.mainHeaderColor ??
                          originalRow?.original.headerColor ??
                          DEFAULT_BASE_COLOR,
                      }
                    : null;

                return (
                  <TimelineDroppableRow
                    key={row.key}
                    isParent={Boolean(isParent)}
                    rowId={rowId}
                    className="flex relative w-full"
                    style={{
                      paddingBottom: isGroupCollapsed ? 0 : rowGap,
                      overflowY: isFirstSubRowOfGroup ? 'visible' : undefined,
                    }}
                    measureElement={rowVirtualizer.measureElement}
                    dataIndex={row.index}
                  >
                    {expandedBand && (
                      <>
                        {/* Fill spans the full row (including the header). */}
                        <div
                          aria-hidden="true"
                          className="absolute left-0 right-0 z-0 pointer-events-none"
                          style={{
                            top: expandedBand.top,
                            height: expandedBand.height,
                            backgroundColor: timelineBgColorWithAlpha(expandedBand.color, 0.14),
                          }}
                        />
                        {/* Top/bottom borders only, across the date-range area (no
                            left/right border, and not over the row header), with
                            rounded corners. */}
                        <div
                          aria-hidden="true"
                          className={cn(
                            'absolute right-0 z-0 pointer-events-none border-t-2 border-b-2',
                            layout?.theme === 'compact' ? 'rounded-sm' : 'rounded-lg',
                          )}
                          style={{
                            top: expandedBand.top,
                            height: expandedBand.height,
                            left: rowHeaderColumnWidth,
                            borderColor: timelineBgColorWithAlpha(expandedBand.color, 0.5),
                          }}
                        />
                      </>
                    )}
                    {fixedColumns.map((fixedColumn, colIndex) => {
                      const width = fixedColumn.column.getSize();
                      const isMainHeaderColumn = colIndex === 0 && hasVerticalSubHeaders;
                      const isFirstSubRowCell =
                        isMainHeaderColumn && originalRow?.original.isFirstSubRow;

                      return (
                        <div
                          key={fixedColumn.id}
                          className={cn(
                            'flex relative transition-all duration-10 ease-in-out bg-bg-surface-base',
                            {
                              'py-0.5':
                                layout?.theme !== 'compact' &&
                                !(isGroupCollapsed && !originalRow?.original.isFirstSubRow),
                              'z-10': isFirstSubRowCell,
                              'z-5': !isFirstSubRowCell,
                            },
                          )}
                          style={{
                            minWidth: width,
                            width,
                            minHeight,
                            maxHeight,
                            overflow: isFirstSubRowCell ? 'visible' : undefined,
                          }}
                          data-index={colIndex}
                        >
                          {flexRender(fixedColumn.column.columnDef.cell, fixedColumn.getContext())}
                        </div>
                      );
                    })}

                    {visibleDateCells.map((cell) => {
                      if (!cell) {
                        return null;
                      }
                      const width = cell.column.getSize();
                      return (
                        <div
                          key={cell.id}
                          className="flex bg-bg-surface-base"
                          style={{ minWidth: width, width: width, minHeight, maxHeight }}
                        >
                          {flexRender<TimelineTableCellContext<TimelineRowType, unknown>>(
                            cell.column.columnDef.cell,
                            {
                              ...cell.getContext(),
                              isRowDraggedOver: rowId === hoveredRowId,
                              isDropForbidden: rowId === forbiddenRowId,
                              extraLanes: extraLaneHeight > 0 ? 1 : 0,
                            },
                          )}
                        </div>
                      );
                    })}
                  </TimelineDroppableRow>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Drag overlay */}
      {createPortal(
        <DragOverlay dropAnimation={null}>
          {activeDragData && (
            <TimelineItemDragOverlayContent
              name={activeDragData.name}
              color={activeDragData.color}
              startDate={activeDragData.startDate}
              endDate={activeDragData.endDate}
              dayWidth={dayWidth}
              isCompact={layout?.theme === 'compact'}
              selectedCount={activeDragData.selectedItemIds?.length ?? 1}
              selectedPreviews={activeDragData.selectedPreviews}
            />
          )}
        </DragOverlay>,
        portalContainer ?? document.body,
      )}
    </DndContext>
  );
};
